import { Button } from 'antd';
import { Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { useCanManageService } from '../../../hooks/use-can-manage-service';
import { buildServiceItemColumns } from '../components/build-service-item-columns';
import { ServiceItemFormDrawer } from '../components/ServiceItemFormDrawer';
import { ServiceItemsTable } from '../components/ServiceItemsTable';
import { useTruckClassOptions } from '../hooks/use-truck-class-options';
import { useServiceItems } from '../hooks/use-service-items';
import type { ServiceItemView } from '../service-items.api';

/** The body of `MaintenancePage`, so no `PageHeader` of its own. Each item carries the truck classes it applies to. Items are deactivated, never deleted: class plans and last-done rows point at them. */
export function ServiceItemsPage() {
  const { state, list, isRefreshing, saveError, saving, resetSaveError, create, update, deactivate, reactivate } = useServiceItems();
  const classes = useTruckClassOptions();
  const canManage = useCanManageService();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceItemView | undefined>(undefined);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  function openDrawer(row?: ServiceItemView) {
    resetSaveError();
    setEditing(row);
    setDrawerOpen(true);
  }

  const actions = canManage ? { onEdit: openDrawer, onDeactivate: deactivate, onReactivate: reactivate } : undefined;
  const newButton = canManage && (
    <Button type="primary" icon={<Plus className="size-4" />} onClick={() => openDrawer()}>
      New service item
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      {newButton && <div className="flex justify-end">{newButton}</div>}
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load service items" description="Something went wrong loading the catalogue. Try reloading the page." />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading service items" withSearch />}
      {state.status === 'loaded' && (
        <ServiceItemsTable
          page={state.page}
          columns={buildServiceItemColumns({ list, classNames: new Map((classes ?? []).map((c) => [c.value, c.label])), actions })}
          list={list}
          isRefreshing={isRefreshing}
          newButton={newButton}
        />
      )}
      <ServiceItemFormDrawer
        open={drawerOpen}
        editing={editing}
        classes={classes}
        error={saveError}
        submitting={saving}
        onSubmit={(input) => (editing ? update(editing.id, input) : create(input))}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
